import { createContext, useContext, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";

const AuthContext = createContext<{
  session: Session | null;
  loading: boolean;
  // True after a password-reset link is opened (Supabase fires PASSWORD_RECOVERY
  // and creates a temporary session). While set, the app shows the set-new-password
  // screen instead of the recovered session's content.
  recovering: boolean;
  endRecovery: () => void;
}>({
  session: null,
  loading: true,
  recovering: false,
  endRecovery: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    let mounted = true;

    // Recover the stored session on load. If it can't be refreshed (expired /
    // already-rotated token), clear it locally so we land cleanly on login.
    (async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();
        if (error) throw error;
        if (mounted) setSession(session);
      } catch {
        await supabase.auth.signOut({ scope: "local" }).catch(() => {});
        if (mounted) setSession(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      setSession(session);
      if (event === "PASSWORD_RECOVERY") setRecovering(true);
      else if (event === "SIGNED_OUT") setRecovering(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{ session, loading, recovering, endRecovery: () => setRecovering(false) }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// `local` scope: sign out this browser only. The default (`global`) revokes every
// session for the user, which would sign them out on their other devices too.
export const signOut = () => supabase.auth.signOut({ scope: "local" });
